import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getRouteApi, Link } from '@tanstack/react-router'
import { DownloadIcon, SearchIcon, XIcon } from 'lucide-react'
import { useEffect, useId, useRef } from 'react'
import { ProductImage } from '@app/components/product-image'
import { Badge } from '@app/components/ui/badge'
import { Button } from '@app/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@app/components/ui/card'
import { Input } from '@app/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableNumCell,
  TableRow,
  TableScroller,
} from '@app/components/ui/table'
import { cn } from '@app/lib/cn'
import { useCsvDownload } from '@app/lib/download'
import { useUrlSearchInput } from '@app/lib/nav'
import { PROSES_NAMES } from '../../../worker/api/production.contract'
import { productionApi, productionKeys } from './api'
import { STATUS_HINT, statusTone, delayLabel, plannedDelayLabel, formatNumber, formatDate } from './format'
import { EmptyState, ErrorState, Progress, ProgressLegend, Skeleton } from './parts'
import type { EmirSummary } from './production-api'

const route = getRouteApi('/_korumali/uretim')

const PAGE_SIZE = 50
/** Emirs above this value get a scrap warning in the list (pairs). */
const FIRE_THRESHOLD = 0

/**
 * Anchor the process board links to. Clicking a board column scrolls here and
 * narrows the list to that process, so the board is a way INTO the list rather
 * than a second, unrelated screen.
 */
export const EMIR_LIST_ANCHOR = 'emir-listesi'

/**
 * Column priority.
 *
 * The shell's sidebar leaves the table ~570px at 834 and ~936px at 1200. The
 * emir number, model, pair count, the progress bar and the status are what the
 * shop floor reads, so they stay at every width; the customer and the delivery
 * date return as space allows.
 */
const COLUMN = {
  customer: 'hidden min-[1140px]:table-cell',
  due: 'hidden lg:table-cell',
} as const

/**
 * Emir list.
 *
 * Shows in-progress emirs the moment the screen opens — the reverse of
 * Korgun's "enter criteria first, then look" layout. Filters narrow the
 * result down; the screen is never started with an empty view.
 *
 * Status, search, process and page are URL search params on `/uretim`, so a
 * filtered list is a link somebody can be sent, and Back walks the filters.
 */
export function EmirList() {
  const search = route.useSearch()
  const navigate = route.useNavigate()
  const searchId = useId()
  const csv = useCsvDownload()

  const status = search.status ?? 'inProgress'
  const query = search.query ?? ''
  const proses = search.proses ?? ''
  const page = search.page ?? 1

  /*
   * The box keeps its own copy and settles into the URL after 300 ms so we
   * don't fire a request on every keystroke; `replace` keeps one search from
   * filling the history.
   */
  const [searchText, setSearchText, flushSearch] = useUrlSearchInput(query, (text) => {
    void navigate({
      search: (prev) => ({ ...prev, query: text === '' ? undefined : text, page: undefined }),
      replace: true,
      resetScroll: false,
    })
  })

  /**
   * Writes filters and returns to page 1. `resetScroll: false` on every
   * search-param write in this card: it sits below the board, below the fold,
   * and the router's default scroll reset threw each chip click (and the
   * search box's debounced commit, mid-typing) back to the top of the page.
   * Text still waiting on the search debounce rides along — a chip click
   * inside the 300 ms window used to silently discard it.
   */
  function setFilters(next: Partial<typeof search>) {
    const text = flushSearch({ commit: false })
    void navigate({
      search: (prev) => ({
        ...prev,
        query: text === '' ? undefined : text,
        ...next,
        page: undefined,
      }),
      resetScroll: false,
    })
  }

  /*
   * Arriving from a board column: the card is below the fold on every screen
   * the factory uses, so a filter that silently applied off-screen looked like
   * a click that did nothing. The hash alone doesn't cover the case where the
   * process changes while the anchor is already the current hash.
   */
  const cardRef = useRef<HTMLElement>(null)
  function scrollListIntoView() {
    cardRef.current?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    })
  }
  useEffect(() => {
    if (proses === '') return
    scrollListIntoView()
  }, [proses])

  const args = { status, query, proses, page, pageSize: PAGE_SIZE }
  const { data, isPending, isError, error, refetch, isPlaceholderData } = useQuery({
    queryKey: productionKeys.emirs(args),
    queryFn: () => productionApi.emirs(args),
    placeholderData: keepPreviousData,
  })

  const statuses = data?.statuses ?? []
  /*
   * Filter-pill name from the fixed table — it used to be scavenged from the
   * visible rows' activeProses, which left a bare "04" whenever the rows
   * didn't carry the process (done/planned emirs, empty results).
   */
  const prosesName = PROSES_NAMES[proses]

  return (
    <Card id={EMIR_LIST_ANCHOR} ref={cardRef} className="scroll-mt-14">
      <CardHeader className="flex-wrap gap-y-2.5">
        <div>
          <CardTitle>Üretim emirleri</CardTitle>
          {/* The planned bucket swaps in its own line — same pattern as the
              siparişler numune note: the moment the odd filter is active is
              the moment to say what it means. */}
          <p className="mt-0.5 text-micro text-ink-muted">
            {status === 'planned'
              ? 'Açılmış ama henüz üretime verilmemiş emirler — bir kısmı uzun süredir bekliyor olabilir.'
              : 'Emir, model, müşteri ve termin; ilerleme çubuğu aşama dağılımını verir.'}
          </p>
          <ProgressLegend className="mt-1.5" />
          {/* Arrived from a board column: says which process, and how to get out. */}
          {proses ? (
            <p className="mt-1.5 inline-flex items-center gap-1.5 rounded-xs border border-kordovan/30 bg-kordovan/8 py-0.5 pl-2 pr-1 text-micro text-kordovan">
              <span className="font-mono tnum">{proses}</span>
              <span>{prosesName ?? 'prosesi'} filtresi</span>
              <button
                type="button"
                onClick={() => setFilters({ proses: undefined })}
                aria-label="Proses filtresini kaldır"
                // 24px touch target (negative margin keeps the pill height); the icon stays small.
                className="-my-1.5 grid size-6 place-items-center rounded-xs hover:bg-kordovan/12"
              >
                <XIcon className="size-3" aria-hidden />
              </button>
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1">
            {statuses.map((option) => (
              <button
                key={option.value}
                type="button"
                title={STATUS_HINT[option.value]}
                onClick={() =>
                  setFilters({ status: option.value === 'inProgress' ? undefined : option.value })
                }
                aria-pressed={status === option.value}
                className={cn(
                  'flex h-8 items-center gap-1.5 rounded-xs border px-2 text-data transition-colors',
                  status === option.value
                    ? 'border-kordovan/30 bg-kordovan/8 font-medium text-kordovan'
                    : 'border-hairline bg-surface text-ink-muted hover:border-ink-muted/45 hover:text-ink',
                )}
              >
                {option.label}
                <span className="font-mono text-micro tnum opacity-70">{formatNumber(option.count)}</span>
              </button>
            ))}
          </div>

          <div className="relative min-w-56">
            <label className="sr-only" htmlFor={searchId}>
              Emir, model veya müşteri ara
            </label>
            <SearchIcon
              aria-hidden
              className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-ink-muted"
            />
            <Input
              id={searchId}
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="Emir no, model veya müşteri"
              className="pl-7"
            />
          </div>

          {/* Exports the whole filtered emir list, not just the page on screen.
              flushSearch: the file must contain what was just typed, not what
              the 300 ms debounce had gotten around to — the commit it fires
              also catches the list up to the same text. */}
          <Button
            variant="outline"
            disabled={csv.busy}
            aria-busy={csv.busy}
            onClick={() => csv.start('/production/export', { status, query: flushSearch(), proses })}
          >
            <DownloadIcon aria-hidden />
            {csv.busy ? 'Hazırlanıyor…' : 'Dışa aktar'}
          </Button>

          {/* Kordovan, not hardal: this is an error, and light-theme hardal at
              3.5:1 was the least readable place to put the only feedback that
              an export failed. */}
          {csv.error ? (
            <span role="alert" className="text-micro text-kordovan">
              {csv.error}
            </span>
          ) : null}
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {isError ? (
          <ErrorState message={error instanceof Error ? error.message : undefined} onRetry={refetch} />
        ) : isPending ? (
          <ListSkeleton />
        ) : data.emirs.length === 0 ? (
          <EmptyState
            title="Bu filtreyle emir yok"
            description={
              query
                ? `"${query}" aramasıyla eşleşen üretim emri bulunamadı. Aramayı temizleyip devam eden emirlere dönebilirsiniz.`
                : proses
                  ? 'Bu proseste seçili durumda üretim emri yok. Proses filtresini kaldırıp listeyi genişletin.'
                  : 'Seçili durumda üretim emri yok. Başka bir durum seçerek listeyi genişletin.'
            }
            action={
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  // The URL is the single writer; the search box follows it.
                  void navigate({
                    search: (prev) => ({
                      ...prev,
                      query: undefined,
                      status: undefined,
                      proses: undefined,
                      page: undefined,
                    }),
                    resetScroll: false,
                  })
                }
              >
                Devam eden emirleri göster
              </Button>
            }
          />
        ) : (
          <>
            <TableScroller className={cn(isPlaceholderData && 'opacity-60')}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">Emir</TableHead>
                    <TableHead className="min-w-36">Model</TableHead>
                    <TableHead className={cn('min-w-32', COLUMN.customer)}>Müşteri</TableHead>
                    {/* "Çift", the unit every other surface names — a bare
                        "Adet" could mean single shoes or asorti cartons. */}
                    <TableHead className="w-18 text-right">Çift</TableHead>
                    <TableHead className="min-w-40">İlerleme</TableHead>
                    <TableHead className={cn('w-28', COLUMN.due)}>Termin</TableHead>
                    <TableHead className="w-28">Durum</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.emirs.map((emir) => (
                    <EmirRow key={emir.emirNo} emir={emir} />
                  ))}
                </TableBody>
              </Table>
            </TableScroller>

            <Pager
              page={data.pagination.page}
              totalPages={data.pagination.totalPages}
              total={data.pagination.total}
              onChange={(next) => {
                void navigate({
                  search: (prev) => ({ ...prev, page: next <= 1 ? undefined : next }),
                  resetScroll: false,
                })
                // The new page starts at the top of the LIST — a full-page
                // scroll reset would land on the board instead.
                scrollListIntoView()
              }}
            />
          </>
        )}
      </CardContent>
    </Card>
  )
}

function EmirRow({ emir }: { emir: EmirSummary }) {
  const planned = emir.status === ''
  // Red only for production actually running late: a planned emir was never
  // released, so its past termin is a stale plan, not a floor holdup — and
  // hundreds of abandoned plans in kordovan drowned out the real ones.
  const overdue = emir.status === 'B' && (emir.delayDays ?? 0) > 0
  // "How many days overdue" is misleading on a closed emir: we don't know the shipping date.
  const delay =
    emir.status === 'X'
      ? null
      : planned
        ? plannedDelayLabel(emir.delayDays)
        : delayLabel(emir.delayDays)

  return (
    <TableRow className="relative">
      <TableCell className="p-0">
        {/* Whole-row link, same idiom as the siparişler list: the ::after
            overlay stretches the anchor over the row, so a tap anywhere —
            not just the narrow emir-number cell — opens the detail. */}
        <Link
          to="/uretim/$emirNo"
          params={{ emirNo: String(emir.emirNo) }}
          className="flex h-9 items-center pl-3.5 pr-2.5 font-mono text-data font-medium text-kordovan tnum after:absolute after:inset-0 after:content-[''] hover:underline"
        >
          {emir.emirNo}
        </Link>
      </TableCell>

      {/* max-w-0: `truncate` implies nowrap, which without it would pin the
          column to the longest model name and push the table into scroll. */}
      <TableCell className="max-w-0">
        {/* The photo leads the model rather than the row: an emir carries
            exactly one SKod and one RKod, so it is the batch's own colourway,
            not a stand-in for the model in general. */}
        <span className="flex items-center gap-2">
          <ProductImage
            imageKey={emir.imageKey}
            alt={`${emir.model ?? emir.modelKod ?? 'Model'} fotoğrafı`}
          />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-ink">{emir.model ?? emir.modelKod ?? '—'}</span>
            <span className="block truncate font-mono text-micro text-ink-muted tnum">
              {emir.modelKod ?? '—'}
              {emir.color ? ` · ${emir.color}` : ''}
            </span>
          </span>
        </span>
      </TableCell>

      <TableCell className={cn('max-w-0', COLUMN.customer)}>
        <span className="block truncate text-ink">{emir.customer ?? '—'}</span>
        <span className="block truncate font-mono text-micro text-ink-muted tnum">
          {emir.sipNo === null ? '—' : `Sipariş ${emir.sipNo}`}
        </span>
      </TableCell>

      <TableNumCell className="font-medium">{formatNumber(emir.totalPairs)}</TableNumCell>

      <TableCell>
        <Progress
          total={emir.totalPairs}
          done={emir.donePairs}
          inProgress={emir.inProgressPairs}
          waiting={emir.waitingPairs}
          notStarted={emir.notStartedPairs}
        />
        <span className="mt-1 flex flex-wrap items-center gap-1">
          {emir.activeProses.length === 0 ? (
            <span className="text-micro text-ink-muted">
              {emir.status === 'X' ? 'Tüm prosesler bitti' : 'Üretime verilmedi'}
            </span>
          ) : (
            emir.activeProses.map((proses) => (
              // Tone alone (steel vs mustard) carried "being worked" vs "all
              // queued"; the title spells the split out.
              <Badge
                key={proses.proses}
                tone={proses.inProgressPairs > 0 ? 'info' : 'warn'}
                title={`${proses.prosesName}: ${formatNumber(proses.waitingPairs)} bekleyen · ${formatNumber(proses.inProgressPairs)} işlemde`}
              >
                <span className="font-mono tnum">{proses.proses}</span>
                {proses.prosesName}
                <span className="font-mono tnum">
                  {formatNumber(proses.waitingPairs + proses.inProgressPairs)}
                </span>
              </Badge>
            ))
          )}
          {emir.fire > FIRE_THRESHOLD ? (
            <Badge tone="accent">Fire {formatNumber(emir.fire)}</Badge>
          ) : null}
        </span>
      </TableCell>

      <TableCell className={cn('font-mono tnum', overdue && 'text-kordovan', COLUMN.due)}>
        <span className="block">{formatDate(emir.deliveryDate)}</span>
        {delay ? (
          <span className={cn('block text-micro', overdue ? 'text-kordovan' : 'text-ink-muted')}>
            {delay}
          </span>
        ) : null}
      </TableCell>

      <TableCell>
        <Badge tone={statusTone(emir.status)} title={planned ? STATUS_HINT.planned : undefined}>
          {emir.statusLabel}
        </Badge>
        {/* Below lg the Termin column is gone, and this was the only overdue
            signal on the row — exactly on the shop-floor tablet widths. */}
        {overdue && delay ? (
          <span className="mt-0.5 block text-micro text-kordovan lg:hidden">{delay}</span>
        ) : null}
      </TableCell>
    </TableRow>
  )
}

function Pager({
  page,
  totalPages,
  total,
  onChange,
}: {
  page: number
  totalPages: number
  total: number
  onChange: (page: number) => void
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-hairline px-3.5 py-2">
      <p className="text-micro text-ink-muted">
        <span className="font-mono tnum">{formatNumber(total)}</span> emir ·{' '}
        <span className="font-mono tnum">{formatNumber(page)}</span> /{' '}
        <span className="font-mono tnum">{formatNumber(totalPages)}</span>. sayfa
      </p>
      <div className="flex gap-1.5">
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1}
          onClick={() => onChange(Math.max(1, page - 1))}
        >
          Önceki
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={page >= totalPages}
          onClick={() => onChange(Math.min(totalPages, page + 1))}
        >
          Sonraki
        </Button>
      </div>
    </div>
  )
}

function ListSkeleton() {
  return (
    <div className="flex flex-col gap-2 px-3.5 py-3">
      {Array.from({ length: 8 }, (_, index) => (
        <div key={index} className="flex items-center gap-3">
          <Skeleton className="h-3.5 w-12" />
          <Skeleton className="h-3.5 w-48" />
          <Skeleton className="h-3.5 w-36" />
          <Skeleton className="h-3.5 w-10" />
          <Skeleton className="h-3.5 flex-1" />
          <Skeleton className="h-3.5 w-20" />
        </div>
      ))}
    </div>
  )
}
